import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface TimeSeriesDecomposition {
  timestamps: string[];
  trend: number[];
  seasonal: number[];
  residual: number[];
}

interface TimeSeriesDecompositionChartProps {
  data: TimeSeriesDecomposition | null;
  component: 'trend' | 'seasonal' | 'residual';
  loading?: boolean;
}

const TimeSeriesDecompositionChart: React.FC<TimeSeriesDecompositionChartProps> = ({ data, component, loading }) => {
  if (loading) {
    return (
      <div className="h-20 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.timestamps.map((timestamp, index) => ({
      time: new Date(timestamp).toLocaleTimeString(),
      value: Math.round(data[component][index] * 10) / 10
    }));
  }, [data, component]);

  const getColor = () => {
    switch (component) {
      case 'trend': return '#6366f1';
      case 'seasonal': return '#10b981';
      case 'residual': return '#ef4444';
      default: return '#6366f1';
    }
  };

  return (
    <div className="w-full h-20 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
          <XAxis 
            dataKey="time" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 8, fill: '#64748b' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 8, fill: '#64748b' }}
          />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke={getColor()}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeSeriesDecompositionChart;